import { ChangeEvent, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import clsx from 'clsx'
import debounce from 'debounce'
import { observer } from 'mobx-react-lite'

import { popularDirectionBeketStore } from 'pages/beket/search-direction/model'

import findTicketFormStore from 'entities/beket/models/find-ticket/find-ticket-store'

import { SearchApiBeket } from 'shared/api/beket/search'
import { CountriesSearchBeket } from 'shared/api/beket/search/model.ts'
import ImageGlass from 'shared/images/glasss.png'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { EmptyResults } from 'shared/ui/empty-result'
import { Header } from 'shared/ui/header'
import { Loader } from 'shared/ui/loader'
import { InfoSameDirection } from 'shared/ui/models'
import { SearchInput } from 'shared/ui/search-input'
import { ItemResult, SearchResultBlock } from 'shared/ui/search-result-block'

import s from './styles.module.scss'

enum TYPE_SEARCH {
  SEARCH = 'search',
  START = 'start',
  LOADING = 'loading',
}

enum DIRECTION {
  FROM = 'from',
  TO = 'to',
}

const BeketSearchDirection = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const { direction } = useParams()

  const [params] = useSearchParams()

  const [listSearch, setListSearch] = useState<CountriesSearchBeket[]>()

  const isSearch = !!params.get('page')

  const [status, setStatus] = useState<TYPE_SEARCH>(TYPE_SEARCH.START)

  if (direction !== DIRECTION.FROM && direction !== DIRECTION.TO) {
    return null
  }

  const popularDirectionsList =
    popularDirectionBeketStore.getPopularDirectionsList()

  const getPopularDirections = () => {
    void popularDirectionBeketStore.fetchPopularDirectionsList()
  }

  const onClick = () => {
    navigate(-1)
  }

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const searchingValue = event?.target?.value

    if (searchingValue?.length) {
      setStatus(TYPE_SEARCH.LOADING)

      try {
        const { data: CountrySearch } =
          await SearchApiBeket.searchCity(searchingValue)
        setStatus(TYPE_SEARCH.SEARCH)

        setListSearch(CountrySearch?.data)
      } catch (e) {
        setListSearch([])
      }
    } else {
      setStatus(TYPE_SEARCH.START)
    }
  }

  const onResetValue = () => {
    setStatus(TYPE_SEARCH.START)
  }

  const onSelectDirection = (data: ItemResult) => {
    const currentForm = isSearch
      ? findTicketFormStore?.copyFormFindTicket?.directions?.[0]
      : findTicketFormStore?.formFindTicket?.directions?.[0]

    const directionData = {
      id: parseInt(data.code!),
      cityName: data.title!,
      countryName: data.subTitle!,
    }

    if (
      direction === DIRECTION.FROM &&
      currentForm?.to?.id !== parseInt(data?.code || '')
    ) {
      findTicketFormStore.onSelectDirectionFrom({
        depp: directionData,
        isCopy: isSearch,
      })
      navigate(-1)
    } else if (
      direction === DIRECTION.TO &&
      currentForm?.from?.id !== parseInt(data?.code || '')
    ) {
      findTicketFormStore.onSelectDirectionTo({
        depp: directionData,
        isCopy: isSearch,
      })
      navigate(-1)
    } else {
      openDialog({
        isDialog: false,
        child: <InfoSameDirection onCloseDirection={closeDialog} />,
      })
    }
  }

  const titlePage = direction === DIRECTION.FROM ? t('from') : t('to')

  useEffect(() => {
    if (!popularDirectionsList) {
      getPopularDirections()
    }
  }, [])

  return (
    <div className={s.container}>
      <div className={s.header}>
        <Header
          title={titlePage}
          actionLeft={
            <AppIcon
              onClick={onClick}
              className={s.icon}
              id={AllIcons.ARROW_LEFT}
            />
          }
        />
      </div>

      <div className={clsx(s.search, 'container')}>
        <SearchInput
          placeholder={t('beket.page.search_by_city')}
          onReset={onResetValue}
          onChange={debounce(onChange, 1000)}
        />
      </div>

      {status === TYPE_SEARCH.LOADING ? <Loader marginTop={120} /> : null}

      {status === TYPE_SEARCH.START && (
        <div>
          <div className={clsx(s.title, 'container')}>
            <p>{t('popular_direction')}</p>
          </div>

          {popularDirectionsList?.map((item, index) => (
            <div
              className={clsx(s.item, 'container')}
              key={index}
            >
              <SearchResultBlock
                onSelect={() =>
                  onSelectDirection({
                    image: item?.flag_url,
                    title: item?.city,
                    subTitle: item?.country,
                    code: String(item?.city_id),
                  })
                }
                {...{
                  image: item?.flag_url,
                  title: item?.country,
                  subTitle: item?.city,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {status === TYPE_SEARCH.SEARCH &&
        listSearch?.map((result, index) => (
          <div
            className={clsx(s.item, 'container')}
            key={index}
          >
            <SearchResultBlock
              onSelect={() => {
                onSelectDirection({
                  title: result?.name,
                  code: String(result?.id || ''),
                  subTitle: result?.country?.name,
                })
              }}
              {...{
                subTitle: result?.name,
                title: result?.country?.name,
              }}
            />
          </div>
        ))}

      {status === TYPE_SEARCH.SEARCH && listSearch?.length === 0 && (
        <div className={clsx('container', s.emptyBlock)}>
          <EmptyResults
            image={
              <img
                src={ImageGlass}
                alt="test"
                className={s.image}
              />
            }
            title={t('not_fount_title')}
            subTitle={t('not_fount_sub_title')}
          />
        </div>
      )}
    </div>
  )
})

export default BeketSearchDirection
